import { api } from '../../shared/api/client'
import type {
  NotificationBadge,
  NotificationCatalog,
  NotificationFilters,
  NotificationListResponse,
  NotificationPreference,
  NotificationPreferenceUpdate,
} from './types'

const unwrap = <T>(response: { data?: { data?: T } | T }): T => {
  const payload = response.data as { data?: T } | T | undefined
  if (payload && typeof payload === 'object' && 'data' in payload && payload.data !== undefined) {
    return payload.data
  }
  return payload as T
}

export const notificationsAPI = {
  list: async (params?: NotificationFilters): Promise<NotificationListResponse> =>
    unwrap<NotificationListResponse>(await api.get('/notifications', { params })),
  getBadge: async (): Promise<NotificationBadge> =>
    unwrap<NotificationBadge>(await api.get('/notifications/badge')),
  getCatalog: async (): Promise<NotificationCatalog> =>
    unwrap<NotificationCatalog>(await api.get('/notifications/catalog')),
  getPreferences: async (): Promise<NotificationPreference[]> => {
    const data = unwrap<{ preferences?: NotificationPreference[] } | NotificationPreference[]>(
      await api.get('/notifications/preferences'),
    )
    return Array.isArray(data) ? data : data.preferences ?? []
  },
  updatePreferences: async (
    preferences: NotificationPreferenceUpdate[],
  ): Promise<NotificationPreference[]> => {
    const data = unwrap<{ preferences?: NotificationPreference[] } | NotificationPreference[]>(
      await api.put('/notifications/preferences', { preferences }),
    )
    return Array.isArray(data) ? data : data.preferences ?? []
  },
  markSeen: () => api.patch('/notifications/seen'),
  markAllAsRead: () => api.patch('/notifications/read-all'),
  markAsRead: (id: string | number) => api.patch(`/notifications/${id}/read`),
  archive: (id: string | number) => api.patch(`/notifications/${id}/archive`),
}

export const activityLogsAPI = {
  getRecentActivities: (limit?: number, userId?: number) => {
    const params = new URLSearchParams()
    if (limit) params.append('limit', limit.toString())
    if (userId) params.append('userId', userId.toString())
    return api.get(`/activity-logs?${params.toString()}`)
  },
  getActivityStats: (days?: number) => api.get(`/activity-logs/stats?days=${days || 7}`),
  getActivitiesByEntity: (entityType: string, entityId: string) =>
    api.get(`/activity-logs/${entityType}/${entityId}`),
}
